import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    linkedSignal,
    model,
    output,
    signal,
    viewChild,
} from "@angular/core";
import { ServiceHourEntry, ServiceHoursService, ServiceHoursStatus } from "./service.hours.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { ServiceHourStatusComponent } from "./service.hour.status.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25Util } from "../../../util/s25-util";
import { Bind } from "../../../decorators/bind.decorator";
import { FormsModule } from "@angular/forms";
import { UserStore } from "../../../stores/user.store";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";

@Component({
    selector: "service-hour-entry",
    template: `
        <div
            class="hour-entry"
            [class.pending]="entry().status === seStatus.Pending"
            [class.denied]="entry().status === seStatus.Denied"
        >
            <div class="entry-header">
                <div class="entry-main">
                    <h3 class="entry-activity">{{ entry().activityName }}</h3>
                    @if (entry().eventName) {
                        <p class="entry-event-name">{{ entry().eventName }}</p>
                    }
                    @if (showContact()) {
                        <p class="entry-contact">{{ entry().contactName }}</p>
                    }
                    <p class="entry-org">{{ entry().organizationName }}</p>
                </div>
                <div class="entry-hours">
                    <span class="hours-value">{{ entry().hours }}</span>
                    <span class="hours-label">hours</span>
                </div>
            </div>
            <div class="entry-details">
                <div class="entry-date">
                    <s25-ng-icon [type]="'calendarDate'" [size]="14" [ariaLabel]="false" />
                    {{ formatDate(entry().serviceDate) }}
                </div>
                <div class="entry-status" [class]="entry().status">
                    @switch (entry().status) {
                        @case (seStatus.Approved) {
                            <s25-ng-icon [type]="'checkCircle'" [size]="14" [ariaLabel]="false" />
                            Approved
                            @if (entry().verifiedByName) {
                                by {{ entry().verifiedByName }}
                            }
                        }
                        @case (seStatus.Pending) {
                            <s25-ng-icon [type]="'clock'" [size]="14" [ariaLabel]="false" />
                            Pending Review
                        }
                        @case (seStatus.Denied) {
                            Denied
                            @if (entry().verifiedByName) {
                                by {{ entry().verifiedByName }}
                            }
                        }
                    }
                </div>
            </div>

            @if (entry().status === seStatus.Denied && entry().denialReason) {
                <p class="denial-reason-display">
                    {{ entry().denialReason }}
                </p>
            }
            @if (entry().description) {
                <p class="entry-description">{{ entry().description }}</p>
            }

            @if (canApprove()) {
                <div class="approval-section">
                    <service-hour-status
                        class="compact"
                        [(status)]="status"
                        [showPending]="false"
                    ></service-hour-status>

                    @if (status() === seStatus.Denied && status() !== entry().status) {
                        <textarea
                            class="denial-reason"
                            placeholder="Reason for denial..."
                            [(ngModel)]="denialReason"
                        ></textarea>
                    }

                    <div class="approval-actions">
                        @if (errorMsg()) {
                            <p class="error-msg">{{ errorMsg() }}</p>
                        }
                        @if (status() !== entry().status && status() === seStatus.Denied) {
                            <s25-ng-button [type]="'primary'" [onClick]="onSaveStatus">Save</s25-ng-button>
                        }
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .hour-entry {
            background: var(--color--surface-bg, white);
            border: 1px solid var(--color--border, rgba(0, 0, 0, 0.15));
            border-radius: 8px;
            padding: 1.25rem;
            transition: all var(--transition-medium, 200ms) ease;
        }

        .hour-entry:hover {
            border-color: var(--color--border-hover, rgba(0, 0, 0, 0.3));
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .hour-entry.pending {
            border-left: 3px solid var(--color--text-warning, #bd5500);
        }

        .hour-entry.denied {
            border-left: 3px solid var(--color--danger, #b00303);
            background: var(--color--surface-bg-error, #ffd4d4);

            /* Muted/link tokens fail 4.5:1 on the error surface */
            .entry-org,
            .entry-details {
                color: var(--color--text, rgba(0, 0, 0, 0.8));
            }

            .entry-event-name {
                color: var(--color--link-text-hover);
            }
        }

        .entry-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 0.75rem;
        }

        .entry-main {
            flex: 1;
        }

        .entry-activity {
            font-size: 1rem;
            font-weight: 600;
            color: var(--color--text-strong, rgba(0, 0, 0, 0.9));
            margin: 0 0 0.25rem 0;
        }

        .entry-event-name {
            font-size: 0.8125rem;
            color: var(--color--link-text, #2573a7);
            margin: 0 0 0.25rem 0;
        }

        .entry-contact {
            font-size: 0.875rem;
            color: var(--color--text, rgba(0, 0, 0, 0.8));
            font-weight: 500;
            margin: 0 0 0.25rem 0;
        }

        .entry-org {
            font-size: 0.875rem;
            color: var(--color--text-muted, #666);
            margin: 0;
        }

        .entry-hours {
            display: flex;
            align-items: baseline;
            gap: 0.25rem;
            padding: 0.5rem 1rem;
            background: var(--color--surface-2-bg, #f0f0f0);
            border-radius: 6px;
        }

        .hours-value {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--color--text-strong, rgba(0, 0, 0, 0.9));
        }

        .hours-label {
            font-size: 0.75rem;
            color: var(--color--text-muted, #666);
        }

        .entry-details {
            display: flex;
            gap: 1.5rem;
            font-size: 0.875rem;
            color: var(--color--text-muted, #666);
            align-items: center;
        }

        .entry-date,
        .entry-status {
            display: flex;
            align-items: center;
            gap: 0.375rem;
        }

        .entry-status.approved {
            color: var(--color--text-success, #2b7c42);
        }

        .entry-status.pending {
            color: var(--color--text-warning, #bd5500);
        }

        .entry-status,
        .denial-reason-display {
            color: var(--color--danger, #b00303);
        }

        .denial-reason-display {
            font-size: 0.875rem;
            padding-top: 0.75rem;
        }

        .entry-description {
            margin: 0.75rem 0 0 0;
            padding-top: 0.75rem;
            border-top: 1px solid var(--color--border, rgba(0, 0, 0, 0.15));
            font-size: 0.875rem;
            color: var(--color--text, rgba(0, 0, 0, 0.8));
            line-height: 1.5;
        }

        .approval-section {
            margin-top: 0.75rem;
            padding-top: 0.75rem;
            border-top: 1px solid var(--color--border, rgba(0, 0, 0, 0.15));
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .denial-reason {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--color--form-border, rgba(0, 0, 0, 0.3));
            border-radius: 6px;
            font-size: 0.875rem;
            resize: vertical;
            min-height: 60px;
            transition: border-color var(--transition-medium, 200ms) ease;
            background: var(--color--form-bg, white);
            color: var(--color--text, rgba(0, 0, 0, 0.8));
        }

        .denial-reason:focus {
            outline: none;
            border-color: var(--color--form-border-hover, rgba(0, 0, 0, 0.6));
            box-shadow: var(--shadow--form-focus);
        }

        .approval-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.75rem;
        }

        .error-msg {
            color: var(--color--danger, #b00303);
            font-size: 0.8125rem;
            margin: 0;
        }

        @media (max-width: 768px) {
            .entry-header {
                flex-direction: column;
                gap: 1rem;
            }

            .entry-hours {
                align-self: flex-start;
            }

            .entry-details {
                flex-direction: column;
                align-items: flex-start;
                gap: 0.5rem;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25IconComponent, ServiceHourStatusComponent, S25ButtonComponent, FormsModule],
})
export class ServiceHourEntryComponent {
    readonly entry = model.required<ServiceHourEntry>();
    readonly canApprove = input.required<boolean>();
    readonly showContact = input<boolean>();

    reviewCompleted = output<void>();

    private readonly statusButtons = viewChild<ServiceHourStatusComponent>(ServiceHourStatusComponent);

    private serviceHoursService = inject(ServiceHoursService);
    private userStore = inject(UserStore);

    protected seStatus = ServiceHoursStatus;

    protected status = linkedSignal<ServiceHoursStatus>(() => this.entry()?.status ?? ServiceHoursStatus.Pending);
    protected denialReason = "";
    protected errorMsg = signal("");

    constructor() {
        effect(() => {
            const status = this.status();
            if (status === ServiceHoursStatus.Approved && status !== this.entry().status) {
                // when approving, start save...if denying, allow user to enter a reason before submit
                return this.onSaveStatus();
            }
        });
    }

    formatDate(dateStr: string): string {
        return S25Datefilter.transform(dateStr, this.userStore.dateFormat()!);
    }

    @Bind
    async onSaveStatus() {
        if (this.status() === this.entry().status) return;
        const approval = this.status() === ServiceHoursStatus.Approved;
        if (approval) this.statusButtons()?.toggleLoading(); // Denials give the option to enter a reason, so set loading on submit button

        const [, error] = await S25Util.Maybe(
            this.serviceHoursService.updateServiceHourStatus(
                this.entry().serviceHourId,
                this.status(),
                this.status() === ServiceHoursStatus.Denied ? this.denialReason : "",
            ),
        );

        if (approval) this.statusButtons()?.toggleLoading();
        if (error) {
            this.errorMsg.set(S25Util.errorText(error)!);
            this.status.set(this.entry().status);
            return;
        }

        this.entry.update((entry) => {
            const updated = { ...entry, status: this.status(), verifiedByName: this.userStore.contactName() };
            updated.denialReason = this.status() === ServiceHoursStatus.Denied ? this.denialReason : "";
            return updated;
        });
        this.errorMsg.set("");
        this.reviewCompleted.emit();
    }
}
